import axios from 'axios';
import { createAuthExpiredError, handleExpiredToken, isAuthExpiredError, isTokenExpired } from '../utils/authToken';
import { Dashboard, Transaction, CreateTransaction, UpdateTransaction, TransactionFilter, Project, Customer, CreateCustomer, UpdateCustomer, CreateProject, UpdateProject, Person, CreatePerson, UpdatePerson, Activity, CreateActivity, UpdateActivity, ActivityLog, ActivityLogFilter, Tag, CreateTag, UpdateTag, CustomerFilter, ProjectPerson, CreateProjectPerson, UpdateProjectPerson, CustomerPerson, CreateCustomerPersonDto, UpdateCustomerPersonDto, LookupItem, PaginationRequest, PaginatedResult, ProjectTag, CreateProjectTag, UpdateProjectTag } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const extractTransactions = (data: any): Transaction[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.transactions)) return data.transactions;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  if (typeof value === 'number') return value !== 0;
  return false;
};

const normalizeProject = (project: any): Project => ({
  ...project,
  id: project.id ?? project.Id,
  title: project.title ?? project.Title ?? '',
  description: project.description ?? project.Description,
  date: project.date ?? project.Date,
  po: project.po ?? project.PO,
  customerId: project.customerId ?? project.CustomerId,
  customerTitle: project.customerTitle ?? project.CustomerTitle ?? '',
  customerGroup: project.customerGroup ?? project.CustomerGroup,
  statusId: project.statusId ?? project.StatusId,
  category: project.category ?? project.Category,
  statusReason: project.statusReason ?? project.StatusReason,
  showInTimeTracking: normalizeBoolean(project.showInTimeTracking ?? project.ShowInTimeTracking),
  isDayOffProject: normalizeBoolean(project.isDayOffProject ?? project.IsDayOffProject),
  transactions: project.transactions ?? project.Transactions ?? [],
  projectTags: project.projectTags ?? project.ProjectTags ?? [],
});

const normalizeCustomer = (customer: any): Customer => ({
  ...customer,
  id: customer.id ?? customer.Id,
  title: customer.title ?? customer.Title ?? '',
  description: customer.description ?? customer.Description,
  date: customer.date ?? customer.Date,
  website: customer.website ?? customer.Website,
  statusId: customer.statusId ?? customer.StatusId,
  type: customer.type ?? customer.Type,
  isArchive: normalizeBoolean(customer.isArchive ?? customer.IsArchive),
  isActive: customer.isActive === undefined && customer.IsActive === undefined ? undefined : normalizeBoolean(customer.isActive ?? customer.IsActive),
  showInTimeTracking: normalizeBoolean(customer.showInTimeTracking ?? customer.ShowInTimeTracking),
  officialId: customer.officialId ?? customer.OfficialId,
  country: customer.country ?? customer.Country,
  longitude: customer.longitude ?? customer.Longitude,
  latitude: customer.latitude ?? customer.Latitude,
  group: customer.group ?? customer.Group,
  projects: (customer.projects ?? customer.Projects ?? []).map(normalizeProject),
  tags: customer.tags ?? customer.Tags ?? [],
  persons: customer.persons ?? customer.Persons ?? [],
});

// Add request interceptor to include JWT token.
// Validates expiry synchronously so an expired token redirects to login
// immediately on user action instead of waiting for a (slow) API round-trip.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      if (isTokenExpired(token)) {
        handleExpiredToken();
        return Promise.reject(createAuthExpiredError());
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration without a full page reload.
// AuthContext listens for the expiry event and switches to the login screen instantly.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if ((error as { __authExpired?: boolean })?.__authExpired) {
      // Already handled in the request interceptor; just propagate.
      return Promise.reject(error);
    }
    if (isAuthExpiredError(error)) {
      // Server rejected the token (401): clear it and notify immediately.
      handleExpiredToken();
    }
    return Promise.reject(error);
  }
);

export const dashboardAPI = {
  getDashboard: (year: number = 0): Promise<Dashboard> =>
    api.get(`/dashboard?year=${year}`).then(res => res.data),
};

export const transactionsAPI = {
  getTransactions: (skip: number = 0, take: number = 50): Promise<Transaction[]> =>
    api.get(`/transactions?skip=${skip}&take=${take}`).then(res => extractTransactions(res.data)),

  getTbuTransactions: (): Promise<Transaction[]> =>
    api.get('/transactions-tbu').then(res => extractTransactions(res.data)),

  getLatestUpdatedTransactions: (year: number = 0, take: number = 25): Promise<Transaction[]> =>
    api.get('/transactions/latest-updated', { params: { year, take } }).then(res => extractTransactions(res.data)),

  getTransactionsFiltered: (filter: TransactionFilter): Promise<{ transactions: Transaction[], count: number }> => {
    const params = new URLSearchParams();

    // Add filter parameters
    if (filter.skip !== undefined) params.append('skip', filter.skip.toString());
    if (filter.take !== undefined) params.append('take', filter.take.toString());
    if (filter.category !== undefined && filter.category !== -2) params.append('category', filter.category.toString());
    if (filter.customerId !== undefined && filter.customerId !== 0) params.append('customerId', filter.customerId.toString());
    if (filter.projectId !== undefined && filter.projectId !== 0) params.append('projectId', filter.projectId.toString());
    if (filter.directionId !== undefined && filter.directionId !== -1) params.append('directionId', filter.directionId.toString());
    if (filter.sourceId !== undefined && filter.sourceId !== -1) params.append('sourceId', filter.sourceId.toString());
    if (filter.currencyId !== undefined && filter.currencyId !== 0) params.append('currencyId', filter.currencyId.toString());
    if (filter.transactionTypeId !== undefined && filter.transactionTypeId !== 0) params.append('typeId', filter.transactionTypeId.toString());
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.searchText) params.append('search', filter.searchText);

    return api.get(`/transactions?${params.toString()}`).then(res => {
      // Backend returns a plain array; older contract expected { transactions, count }.
      // Normalize both shapes so callers never receive undefined transactions.
      const transactions = extractTransactions(res.data);
      const count = Array.isArray(res.data)
        ? transactions.length
        : (res.data?.count ?? res.data?.totalCount ?? transactions.length);
      return { transactions, count };
    });
  },

  getTransaction: (id: number): Promise<Transaction> =>
    api.get(`/transactions/${id}`).then(res => res.data),

  getTransactionsByProject: (projectId: number): Promise<Transaction[]> =>
    api.get(`/transactions/project/${projectId}`).then(res => res.data),

  getUnpaidTransactions: (customerId: number): Promise<Transaction[]> =>
    api.get(`/transactions/unpaid/${customerId}`).then(res => res.data),

  createTransaction: (transaction: CreateTransaction): Promise<Transaction> =>
    api.post('/transactions', transaction).then(res => res.data),

  updateTransaction: (id: number, transaction: UpdateTransaction): Promise<Transaction> =>
    api.put(`/transactions/${id}`, transaction).then(res => res.data),

  deleteTransaction: (id: number): Promise<void> =>
    api.delete(`/transactions/${id}`).then(res => res.data),

  getMinDate: (): Promise<string> =>
    api.get('/transactions/min-date').then(res => res.data),
};

export interface ProjectsFilter {
  skip?: number;
  take?: number;
  customerId?: number;
  statusId?: number;
  searchText?: string;
  tagIds?: number[];
  onlyWithoutTransactions?: boolean;
}

export const projectsAPI = {
  getProjects: (filter: ProjectsFilter = {}): Promise<{ items: Project[]; totalCount: number }> => {
    const { tagIds, ...rest } = filter;
    const params: Record<string, any> = { ...rest };
    if (tagIds && tagIds.length > 0) {
      params['tagIds'] = tagIds;
    }
    return api.get('/projects', { params, paramsSerializer: { indexes: null } }).then(res => ({
      items: (res.data.items ?? res.data).map(normalizeProject),
      totalCount: res.data.totalCount ?? (res.data.items ?? res.data).length,
    }));
  },

  getProject: (id: number): Promise<Project> =>
    api.get(`/projects/${id}`).then(res => normalizeProject(res.data)),

  getProjectsByCustomer: (customerId: number): Promise<Project[]> =>
    api.get(`/projects/customer/${customerId}`).then(res => res.data.map(normalizeProject)),

  getProjectTransactions: (id: number, pagination?: PaginationRequest): Promise<PaginatedResult<Transaction>> =>
    api.get(`/projects/${id}/transactions`, {
      params: {
        pageNumber: pagination?.pageNumber || 1,
        pageSize: pagination?.pageSize || 10,
        sortBy: pagination?.sortBy || 'date',
        sortDescending: pagination?.sortDescending !== false
      }
    }).then(res => res.data),

  exportProjectTransactions: (id: number): Promise<Blob> =>
    api.get(`/projects/${id}/transactions/export`, { responseType: 'blob' }).then(res => res.data),

  createProject: (project: CreateProject): Promise<Project> =>
    api.post('/projects', project).then(res => normalizeProject(res.data)),

  updateProject: (id: number, project: UpdateProject): Promise<Project> =>
    api.put(`/projects/${id}`, project).then(res => normalizeProject(res.data)),

  deleteProject: (id: number): Promise<void> =>
    api.delete(`/projects/${id}`).then(res => res.data),
};

export const customersAPI = {
  getCustomers: (filter?: CustomerFilter): Promise<Customer[]> => {
    if (!filter) {
      return api.get('/customers').then(res => res.data.map(normalizeCustomer));
    }

    const params = new URLSearchParams();

    if (filter.statusId !== undefined) params.append('statusId', filter.statusId.toString());
    if (filter.type !== undefined) params.append('type', filter.type.toString());
    if (filter.isArchive !== undefined) params.append('isArchive', filter.isArchive.toString());
    if (filter.tagIds && filter.tagIds.length > 0) {
      filter.tagIds.forEach(tagId => params.append('tagIds', tagId.toString()));
    }
    if (filter.searchText) params.append('searchText', filter.searchText);
    if (filter.skip !== undefined) params.append('skip', filter.skip.toString());
    if (filter.take !== undefined) params.append('take', filter.take.toString());

    return api.get(`/customers?${params.toString()}`).then(res => res.data.map(normalizeCustomer));
  },

  getCustomer: (id: number): Promise<Customer> =>
    api.get(`/customers/${id}`).then(res => normalizeCustomer(res.data)),

  createCustomer: (customer: CreateCustomer): Promise<Customer> =>
    api.post('/customers', customer).then(res => normalizeCustomer(res.data)),

  updateCustomer: (id: number, customer: UpdateCustomer): Promise<Customer> =>
    api.put(`/customers/${id}`, customer).then(res => normalizeCustomer(res.data)),

  deleteCustomer: (id: number): Promise<void> =>
    api.delete(`/customers/${id}`).then(res => res.data),
};

export const activitiesAPI = {
  getActivities: (): Promise<Activity[]> =>
    api.get('/activity').then(res => res.data),

  getActivity: (id: number): Promise<Activity> =>
    api.get(`/activity/${id}`).then(res => res.data),

  getActivitiesByReference: (referenceType: string, referenceId: number): Promise<Activity[]> =>
    api.get(`/activity/reference/${referenceType}/${referenceId}`).then(res => res.data),

  createActivity: (activity: CreateActivity): Promise<Activity> =>
    api.post('/activity', activity).then(res => res.data),

  updateActivity: (id: number, activity: UpdateActivity): Promise<Activity> =>
    api.put(`/activity/${id}`, activity).then(res => res.data),

  deleteActivity: (id: number): Promise<void> =>
    api.delete(`/activity/${id}`).then(res => res.data),
};

export const activityLogsAPI = {
  getActivityLogs: (filter?: ActivityLogFilter): Promise<ActivityLog[]> =>
    api.get('/activitylogs', { params: filter }).then(res => res.data),
};

export const personsAPI = {
  getPersonsForReview: (): Promise<Person[]> =>
    api.get('/persons/review').then(res => res.data),

  getPersons: (): Promise<Person[]> =>
    api.get('/persons').then(res => res.data),

  getPerson: (id: number): Promise<Person> =>
    api.get(`/persons/${id}`).then(res => res.data),

  createPerson: (person: CreatePerson): Promise<Person> =>
    api.post('/persons', person).then(res => res.data),

  updatePerson: (id: number, person: UpdatePerson): Promise<Person> =>
    api.put(`/persons/${id}`, person).then(res => res.data),

  deletePerson: (id: number): Promise<void> =>
    api.delete(`/persons/${id}`).then(res => res.data),
};

const normalizeTag = (tag: any): Tag => ({
  id: tag.id || tag.Id,
  name: tag.name || tag.Name || '',
});

const normalizeProjectTag = (tag: any): ProjectTag => ({
  id: tag.id || tag.Id,
  name: tag.name || tag.Name || '',
});

export const lookupsAPI = {
  getTransactionDirections: (): Promise<LookupItem[]> =>
    api.get('/lookups/transaction-directions').then(res => res.data),
  getTransactionTypes: (): Promise<LookupItem[]> =>
    api.get('/lookups/transaction-types').then(res => res.data),
  getTransactionCategories: (): Promise<LookupItem[]> =>
    api.get('/lookups/transaction-categories').then(res => res.data),
};

export const tagsAPI = {
  getTags: (): Promise<Tag[]> =>
    api.get('/tags').then(res => res.data.map(normalizeTag)),

  getTag: (id: number): Promise<Tag> =>
    api.get(`/tags/${id}`).then(res => normalizeTag(res.data)),

  createTag: (tag: CreateTag): Promise<Tag> =>
    api.post('/tags', tag).then(res => normalizeTag(res.data)),

  updateTag: (id: number, tag: UpdateTag): Promise<Tag> =>
    api.put(`/tags/${id}`, tag).then(res => normalizeTag(res.data)),

  deleteTag: (id: number): Promise<void> =>
    api.delete(`/tags/${id}`).then(res => res.data),
};

export const projectTagsAPI = {
  getProjectTags: (): Promise<ProjectTag[]> =>
    api.get('/projecttags').then(res => res.data.map(normalizeProjectTag)),

  getProjectTag: (id: number): Promise<ProjectTag> =>
    api.get(`/projecttags/${id}`).then(res => normalizeProjectTag(res.data)),

  createProjectTag: (tag: CreateProjectTag): Promise<ProjectTag> =>
    api.post('/projecttags', tag).then(res => normalizeProjectTag(res.data)),

  updateProjectTag: (id: number, tag: UpdateProjectTag): Promise<ProjectTag> =>
    api.put(`/projecttags/${id}`, tag).then(res => normalizeProjectTag(res.data)),

  deleteProjectTag: (id: number): Promise<void> =>
    api.delete(`/projecttags/${id}`).then(res => res.data),
};

export const projectPersonsAPI = {
  getProjectPersons: (): Promise<ProjectPerson[]> =>
    api.get('/projectpersons').then(res => res.data),

  getProjectPerson: (id: number): Promise<ProjectPerson> =>
    api.get(`/projectpersons/${id}`).then(res => res.data),

  getProjectPersonsByProject: (projectId: number): Promise<ProjectPerson[]> =>
    api.get(`/projectpersons/project/${projectId}`).then(res => res.data),

  getProjectPersonsByPerson: (personId: number): Promise<ProjectPerson[]> =>
    api.get(`/projectpersons/person/${personId}`).then(res => res.data),

  createProjectPerson: (projectPerson: CreateProjectPerson): Promise<ProjectPerson> =>
    api.post('/projectpersons', projectPerson).then(res => res.data),

  updateProjectPerson: (id: number, projectPerson: UpdateProjectPerson): Promise<ProjectPerson> =>
    api.put(`/projectpersons/${id}`, projectPerson).then(res => res.data),

  deleteProjectPerson: (id: number): Promise<void> =>
    api.delete(`/projectpersons/${id}`).then(res => res.data),
};

export const customerPersonsAPI = {
  getCustomerPersons: (): Promise<CustomerPerson[]> =>
    api.get('/customerpersons').then(res => res.data),

  getCustomerPerson: (id: number): Promise<CustomerPerson> =>
    api.get(`/customerpersons/${id}`).then(res => res.data),

  getCustomerPersonsByCustomer: (customerId: number): Promise<CustomerPerson[]> =>
    api.get(`/customerpersons/customer/${customerId}`).then(res => res.data),

  getCustomerPersonsByPerson: (personId: number): Promise<CustomerPerson[]> =>
    api.get(`/customerpersons/person/${personId}`).then(res => res.data),

  createCustomerPerson: (customerPerson: CreateCustomerPersonDto): Promise<CustomerPerson> =>
    api.post('/customerpersons', customerPerson).then(res => res.data),

  updateCustomerPerson: (id: number, customerPerson: UpdateCustomerPersonDto): Promise<CustomerPerson> =>
    api.put(`/customerpersons/${id}`, customerPerson).then(res => res.data),

  deleteCustomerPerson: (id: number): Promise<void> =>
    api.delete(`/customerpersons/${id}`).then(res => res.data),
};

export { api };
